'use client';

import { CONVERSATIONS_PAGE_SIZE_OPTIONS } from '@/@studio-ai/constants';
import type { ReportsTabProps } from '@/@studio-ai/props';
import { Button, Select, SelectItem, Spinner } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useCallback, useState } from 'react';
import { ReportFolderItem } from './report-folder-item';
import { ReportItem } from './report-item';

export function ReportsTab({
    folders,
    folderedReports,
    folderReportTotals,
    folderReportParams,
    folderReportFetching,
    onFolderPageChange,
    onFolderLimitChange,
    unfiledReports,
    unfiledIsFetching,
    expandedFolders,
    isLoading,
    onToggleExpand,
    onOpen,
    onRename,
    onDelete,
    onShare,
    onMoveToFolder,
    onRenameFolder,
    onDeleteFolder,
    allFolders,
    page,
    totalPages,
    totalUnfiled,
    limit,
    onPageChange,
    onLimitChange,
}: ReportsTabProps) {
    const [dragOverFolderId, setDragOverFolderId] = useState<number | null>(null);

    const handleFolderDrop = useCallback(
        (e: React.DragEvent, folderId: number) => {
            e.preventDefault();
            setDragOverFolderId(null);
            const conversationId = e.dataTransfer.getData('text/plain');
            if (conversationId) {
                onMoveToFolder(conversationId, folderId);
            }
        },
        [onMoveToFolder],
    );

    const handleUnfiledDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            setDragOverFolderId(null);
            const conversationId = e.dataTransfer.getData('text/plain');
            if (conversationId) {
                onMoveToFolder(conversationId, null);
            }
        },
        [onMoveToFolder],
    );

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-24">
                <Spinner size="lg" color="primary" />
                <p className="mt-4 text-small text-default-400">Loading...</p>
            </div>
        );
    }

    const totalCount = folders.length + unfiledReports.length;
    if (totalCount === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-24">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-default-100">
                    <Icon icon="lucide:file-text" width={28} className="text-default-300" />
                </div>
                <p className="mt-4 text-base font-medium text-default-600">No reports yet</p>
                <p className="mt-1 text-small text-default-400">
                    Reports you generate will appear here
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            {/* Folders */}
            {folders.map((folder) => (
                <ReportFolderItem
                    key={folder.id}
                    folder={folder}
                    reports={folderedReports[folder.id] || []}
                    isExpanded={expandedFolders.has(folder.id)}
                    isDragOver={dragOverFolderId === folder.id}
                    isFetching={folderReportFetching[folder.id] ?? false}
                    allFolders={allFolders}
                    page={folderReportParams[folder.id]?.page ?? 1}
                    totalPages={folderReportTotals[folder.id]?.totalPages ?? 1}
                    totalItems={folderReportTotals[folder.id]?.total ?? 0}
                    limit={folderReportParams[folder.id]?.limit ?? 50}
                    onPageChange={(p) => onFolderPageChange(folder.id, p)}
                    onLimitChange={(l) => onFolderLimitChange(folder.id, l)}
                    onToggle={() => onToggleExpand(folder.id)}
                    onReportSelect={onOpen}
                    onReportDelete={onDelete}
                    onReportRename={onRename}
                    onReportShare={onShare}
                    onMoveReportToFolder={onMoveToFolder}
                    onRenameFolder={() => onRenameFolder(folder)}
                    onDeleteFolder={() => onDeleteFolder(folder)}
                    onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        setDragOverFolderId(folder.id);
                    }}
                    onDragLeave={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                            setDragOverFolderId(null);
                        }
                    }}
                    onDrop={(e) => handleFolderDrop(e, folder.id)}
                />
            ))}

            {/* Unfiled reports */}
            {(unfiledReports.length > 0 || folders.length > 0 || unfiledIsFetching) && (
                <>
                    {folders.length > 0 && (
                        <div className="flex items-center gap-2 px-1 pt-2">
                            <Icon icon="lucide:file-text" width={14} className="text-default-400" />
                            <span className="text-mini font-medium uppercase tracking-wider text-default-400">
                                Unfiled
                            </span>
                        </div>
                    )}
                    {unfiledIsFetching && unfiledReports.length === 0 ? (
                        <div className="flex items-center justify-center rounded-xl border border-default-200 py-6">
                            <Spinner size="sm" color="primary" />
                            <span className="ml-2 text-small text-default-400">Loading...</span>
                        </div>
                    ) : (
                        <div
                            className={`divide-y divide-default-200 rounded-xl border transition-colors ${dragOverFolderId === -1 ? 'border-primary bg-primary/5' : 'border-default-200'}`}
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = 'move';
                                setDragOverFolderId(-1);
                            }}
                            onDragLeave={(e) => {
                                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                                    setDragOverFolderId(null);
                                }
                            }}
                            onDrop={handleUnfiledDrop}
                        >
                            {unfiledReports.length > 0 ? (
                                unfiledReports.map((r) => (
                                    <div
                                        key={r.id}
                                        draggable
                                        onDragStart={(e) => {
                                            e.dataTransfer.setData('text/plain', r.conversationId);
                                            e.dataTransfer.effectAllowed = 'move';
                                        }}
                                    >
                                        <ReportItem
                                            report={r}
                                            onSelect={() => onOpen(r)}
                                            onRename={() => onRename(r.conversationId)}
                                            onDelete={() => onDelete(r.conversationId)}
                                            onShare={() => onShare(r.conversationId)}
                                            folders={allFolders}
                                            onMoveToFolder={(fid) =>
                                                onMoveToFolder(r.conversationId, fid)
                                            }
                                        />
                                    </div>
                                ))
                            ) : (
                                <p className="px-4 py-3 text-center text-small text-default-400">
                                    Drop reports here to unfile them
                                </p>
                            )}
                        </div>
                    )}
                </>
            )}

            {/* Pagination controls */}
            {totalUnfiled > 0 && (
                <div className="flex items-center justify-between border-t border-divider pt-4">
                    <div className="flex items-center gap-2">
                        <span className="text-mini text-default-400">Show</span>
                        <Select
                            size="sm"
                            selectedKeys={[String(limit)]}
                            onSelectionChange={(keys) => {
                                const val = Number(Array.from(keys)[0]);
                                if (val) onLimitChange(val);
                            }}
                            classNames={{ base: 'w-20', trigger: 'h-7 min-h-7' }}
                            aria-label="Items per page"
                        >
                            {CONVERSATIONS_PAGE_SIZE_OPTIONS.map((opt) => (
                                <SelectItem key={String(opt)}>{String(opt)}</SelectItem>
                            ))}
                        </Select>
                        <span className="text-mini text-default-400">
                            of {totalUnfiled} unfiled
                        </span>
                    </div>
                    {totalPages > 1 && (
                        <div className="flex items-center gap-1">
                            <Button
                                size="sm"
                                variant="flat"
                                isIconOnly
                                isDisabled={page <= 1}
                                onPress={() => onPageChange(page - 1)}
                                className="h-7 w-7 min-w-7"
                                aria-label="Previous page"
                            >
                                <Icon icon="lucide:chevron-left" width={14} />
                            </Button>
                            <span className="px-2 text-small text-default-600">
                                {page} / {totalPages}
                            </span>
                            <Button
                                size="sm"
                                variant="flat"
                                isIconOnly
                                isDisabled={page >= totalPages}
                                onPress={() => onPageChange(page + 1)}
                                className="h-7 w-7 min-w-7"
                                aria-label="Next page"
                            >
                                <Icon icon="lucide:chevron-right" width={14} />
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
